import JsSdkBase from '../core/js-sdk-base';
import { FunctionKeys } from '../interface';
import { captureSdkResult, sdkLogger } from '../utils';
import { isFunction } from '@libs/shared';

/**
 * fallback装饰器：对于 jssdk API 调用失败后需要fallback到网页环境下的兜底逻辑的 API，可以加上该API
 * 目前仅定位需要执行这种兜底逻辑
 * @param argsModifier 一个针对原始参数的修改器，先获取原始 originArgs（数组类型），然后返回包装过后的参数。支持从XxxSdk中自定义修改API参数。
 *                     不过各个API的参数各不相同，有的是单参数，有的是多参数，所以如果需要修改的话，需要根据具体API自行判断修改方式
 * @see https://project.feishu.cn/b2rl2h/issue/detail/6247009115
 * @see FXD-67861
 * @see https://project.feishu.cn/b2rl2h/assignment/detail/5982459193\
 */
export function jsSdkFallback(argsModifier?: (originArgs: any[]) => any[]) {
    /**
     * @param _ 类的实例
     * @param propKey 类的属性名
     * @param descriptor 类的属性描述符
     */
    return function (
        _: JsSdkBase,
        propKey: FunctionKeys<JsSdkBase>,
        descriptor: PropertyDescriptor
    ) {
        const originalJsSdkApi = descriptor.value;
        type OriginalApiType = typeof originalJsSdkApi;
        descriptor.value = function (
            ...args: Parameters<OriginalApiType>
        ): ReturnType<OriginalApiType> {
            // 因为只给函数成员加该鉴权装饰器，所以 fallbackApi 一定是函数类型
            const fallbackApi = JsSdkBase.prototype[propKey];
            const jsSdkApiResult = originalJsSdkApi.apply(this, args);
            if (jsSdkApiResult instanceof Promise) {
                return jsSdkApiResult
                    .then((res: any) => {
                        return res;
                    })
                    .catch((err: any) => {
                        sdkLogger.error(`${this.sdkType} ${propKey} fail, try to fallback`, {
                            err
                        });
                        const finalArgs = isFunction(argsModifier) ? argsModifier(args) : args;
                        const result = fallbackApi.apply(this, finalArgs);
                        return captureSdkResult({
                            result: result,
                            sdkType: this.sdkType,
                            sdkApi: propKey,
                            prefix: 'fallback'
                        });
                    });
            }
            return jsSdkApiResult;
        };

        return descriptor;
    };
}
